import { IServerSideGetRowsParams } from '@ag-grid-enterprise/all-modules';
import { Box, makeStyles } from '@material-ui/core';
import { navigate } from '@reach/router';
import { RowNode } from 'ag-grid-community';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';

import TableGrid from '../../../components/TableGrid';
import TablePagination from '../../../components/TableGrid/Pagination';
import { refreshTableFn } from '../../../components/TableGrid/TableGridApiContext/TableGridApiContexConstructor';
import {
  useTableGridApiDispatch,
  useTableGridApiState,
} from '../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import DataGridToolbar from '../../../components/TableGrid/TableGridToolbar';
import ActionBox from '../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox';
import { ActionLink } from '../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox.type';
import CountBox from '../../../components/TableGrid/TableGridToolbar/CountBox/CountBox';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { User } from '../../../contexts/AuthContext/authContext.types';
import { api } from '../../../core/api';
import { mapFilterModel } from '../../../core/utils/tableGrid.utils';
import { border } from '../../../core/utils/theme.utils';
import { PageOfQueryDto, QueryDto } from '../../../generated/model/models';
import { ROUTES } from '../../../routes';
import { useCompaniesDispatch } from '../../FinancialData/shared/CompanyContext/CompanyContext';
import { CompanyContextActionTypes } from '../../FinancialData/shared/CompanyContext/CompanyContext.types';
import {
  useFilterDispatch,
  useFilterState,
} from '../../FinancialData/shared/FinancialDataContext/FinancialDataContext';
import {
  AgGridSortModel,
  FinancialDataContextActionTypes,
  RequestParams,
} from '../../FinancialData/shared/FinancialDataContext/FinancialDataContext.types';
import { useQueryListDispatch, useQueryListState } from '../Context/QueryListContext';
import { QueryListContextActionTypes } from '../Context/QueryListContext.types';
import { getQueryListTableColumns } from './QueryListTableColumns';

interface Props {
  datasource: (params: any) => Promise<PageOfQueryDto>;
  adminView: boolean;
  className?: any;
}

const defaultColumnProp = {
  sortable: false,
  resizable: true,
  flex: 1,
  menuTabs: [],
  minWidth: 55,
};

const getActionItems = (
  selectedRows: string[],
  refreshTable: () => void,
  isSomeReportEmpty: boolean,
  isAllReportEmpty: boolean,
): ActionLink[] => [
  {
    title: 'queryList:deleteSelectedItems',
    onClick: async () => {
      ReactGA.event({ category: 'MY_SEARCHES', action: 'DELETE_SELECTED_CLICK' });
      await api.search.deleteSelectedReports(selectedRows, refreshTable);
    },
    count: selectedRows.length,
    isNotActive: false,
    isHidden: false,
  },
  {
    title: isSomeReportEmpty
      ? 'queryList:selectedReportsHaveNotAllReports'
      : 'queryList:downloadSelectedReports',
    snackbarReportText: 'companies:snackbarReportBothText',
    onClick: async () => {
      ReactGA.event({ category: 'MY_SEARCHES', action: 'DOWNLOAD_SELECTED_CLICK' });
      await api.search.downloadSelectedReports(selectedRows);
    },
    count: selectedRows.length,
    isNotActive: isAllReportEmpty,
    isHidden: false,
  },
];

export const QueryListTable: React.FC<Props> = ({ datasource, adminView, className }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { user } = useAuth();
  const dispatch = useFilterDispatch();
  const dispatchCompanies = useCompaniesDispatch();
  const { saveFilterButtonState } = useFilterState();
  const { tableGridApi, selectedRows } = useTableGridApiState();
  const dispatchTableApi = useTableGridApiDispatch();
  const { checkedQueryList } = useQueryListState();
  const dispatchQueryList = useQueryListDispatch();
  const [queryList, setQueryList] = useState<QueryDto[]>([]);
  const [isAllReportEmpty, setIsAllReportEmpty] = useState<boolean>(false);
  const [isSomeReportEmpty, setSomeReportEmpty] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    page: 1,
    size: 20,
    totalPages: 0,
    totalElements: 0,
  });
  const refreshTable = useCallback(() => refreshTableFn(tableGridApi, dispatchTableApi), [
    tableGridApi,
    dispatchTableApi,
  ]);

  const onCellClicked = useMemo(
    () => (data: QueryDto) => {
      if (data?.searchRequest) {
        if (data.sortModel) {
          dispatch({
            type: FinancialDataContextActionTypes.SetSortModel,
            payload: data.sortModel as AgGridSortModel,
          });
        }
        dispatchCompanies({ type: CompanyContextActionTypes.ResetPagination });
        dispatch({
          type: FinancialDataContextActionTypes.Set,
          payload: data.searchRequest,
        });
        ReactGA.event({ category: 'MY_SEARCHES', action: 'QUERY_ROW_CLICK' });
        navigate(ROUTES.financialData.page);
        // navigate(ROUTES.mySearches.page);
      }
    },
    [dispatch, dispatchCompanies],
  );

  const columns = useMemo(() => getQueryListTableColumns(t, onCellClicked, user, adminView), [
    t,
    onCellClicked,
    user,
    adminView,
  ]);

  useEffect(() => {
    if (saveFilterButtonState) {
      refreshTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFilterButtonState]);

  useEffect(() => {
    if (selectedRows) {
      const mergedArray = queryList.concat(checkedQueryList);
      const filtered: any[] =
        (selectedRows || []).map((item: string) =>
          mergedArray.find((uItem: QueryDto) => uItem.id === item),
        ) || [];

      dispatchQueryList({
        type: QueryListContextActionTypes.UpdateCheckedQueryList,
        payload: filtered,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  useEffect(() => {
    if (checkedQueryList.length) {
      const isSomeReportEmpty = checkedQueryList.some((item: any) => {
        return !item.rawData && !item.marketingReport;
      });
      setSomeReportEmpty(isSomeReportEmpty);

      const isAllEpmty = checkedQueryList.every((item: any) => {
        return !item.rawData && !item.marketingReport;
      });

      setIsAllReportEmpty(isAllEpmty);
    }
  }, [checkedQueryList]);

  const actionItems = useMemo(
    () => getActionItems(selectedRows, refreshTable, isSomeReportEmpty, isAllReportEmpty),
    [selectedRows, refreshTable, isSomeReportEmpty, isAllReportEmpty],
  );

  return (
    <Box className={classNames([classes.root, className])}>
      <TableGrid
        translationKey="companies:resultTable"
        tableToolbar={
          <DataGridToolbar>
            <CountBox
              boxTitle={'app:datagrid:savedSearches'}
              totalCount={pagination.totalElements}
              selectedCount={selectedRows.length}
            />
            <ActionBox buttonTitle={t('app:action')} actionItems={actionItems} />
          </DataGridToolbar>
        }
        columns={columns}
        getDatasource={getDatasource(datasource, adminView, user)}
        isShowSideBar={false}
        suppressTableContextProvider={true}
        rowSelection={true}
        rowSelectionIdGetter={rowSelectionIdGetter}
        originalProps={{
          context: {
            pagination,
            setPagination,
            setQueryList,
          },
          rowModelType: 'serverSide',
          pagination: true,
          paginationPageSize: pagination.size,
          cacheBlockSize: pagination.size,
          maxBlocksInCache: 1,
          suppressPaginationPanel: true,
          suppressRowClickSelection: true,
          rowSelection: 'multiple',
          defaultColDef: defaultColumnProp,
        }}
      >
        {pagination.totalElements > 0 ? (
          <TablePagination
            gridApi={tableGridApi}
            pagination={pagination}
            onChange={(value: number) => {
              setPagination({
                ...pagination,
                page: value,
              });
            }}
          />
        ) : null}
      </TableGrid>
    </Box>
  );
};

export const rowSelectionIdGetter = (node: RowNode) => {
  return node.data?.id;
};

export function getDatasource(datasource: any, adminView: boolean, user: User | undefined) {
  let filterObject = {};
  let page = 0;
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      const model: any = params.api.getModel();
      const { pagination, setPagination, setQueryList } = model
        .getContext()
        .getBean('gridOptions').context;
      let totalPages = 0;
      let totalElements = 0;

      // if user is not adminView and usernmae and companyId filters are not set, show no rows overlay
      if (
        adminView &&
        user?.isAdminUser &&
        (!params.api.getFilterModel().username || !params.api.getFilterModel().companyId)
      ) {
        params.api.showNoRowsOverlay();
        params.successCallback([], 0);
        return;
      }

      // if filter was changed redirect to first table page
      if (JSON.stringify(filterObject) !== JSON.stringify(params.api.getFilterModel())) {
        page = 0;
      } else {
        page = pagination.page > 0 ? pagination.page - 1 : 0;
      }
      filterObject = params.api.getFilterModel();

      const requestParams: RequestParams = {
        page: page,
        size: pagination.size,
        filter: mapFilterModel(params.request.filterModel),
      };

      try {
        let response: PageOfQueryDto = {};

        const firstCallResponse: PageOfQueryDto = await datasource(requestParams);
        response = firstCallResponse;

        // Call second api if numbersOfelements is epmty and reload to first page
        if (firstCallResponse.numberOfElements === 0 && requestParams.page !== 0) {
          page = 0;
          response = await datasource({
            page: page,
            size: pagination.size,
            filter: mapFilterModel(params.request.filterModel),
          });
        }

        if (response) {
          const result = response?.content ?? [];

          if (result.length <= 0) {
            params.api.showNoRowsOverlay();
            params.successCallback([], 0);
          } else {
            params.api.hideOverlay();
            setQueryList(result);
            totalElements = response?.totalElements!;
            totalPages = response?.totalPages!;
            params.successCallback(result, totalElements);

            // TODO: refactor to use one request to BE instead of two
            const currentPage = requestParams?.page ?? 0;
            if (params.request.startRow !== currentPage * pagination.size) {
              params.api.paginationGoToPage(currentPage);
            }
          }
        } else {
          params.failCallback();
        }
      } catch {
        params.api.showNoRowsOverlay();
        params.successCallback([], 0);
      } finally {
        page = page + 1; // set Page for pagination component
        setPagination({
          ...pagination,
          page,
          totalPages,
          totalElements,
        });
      }
    },
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    borderLeft: border(theme.palette.divider),
    borderRight: border(theme.palette.divider),
    overflow: 'hidden',

    '& .ag-theme-alpine': {
      borderLeft: 0,
      borderRight: 0,
    },
  },
}));
